/**
 * Todo service
 * @module Todo
 */
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { ITodoMsgContent, TodoCategory, TodoElem, TodoMsgContent, TodoPersonalContent, TodoRoomMsgContent } from '../../models/todo.model';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { UtilitiesService } from 'rainbow-web-sdk/services/utilities/utilities.service';
import xmlParser from 'rainbow-web-sdk/utils/xmlParser';
import { ConversationService, ConversationServiceEvents, ConversationServiceRB } from 'rainbow-web-sdk/services/conversation/conversation.service';
import { Service } from 'rainbow-web-sdk/services/service';
import { RBError, RBErrorCode } from '../../models/common';
import { MessageID } from '../../models';

/** @internal */
export const TODO_SVC = 'TodoService';

/**
 * TodoServiceRB events list
 * @eventProperty
 */
export enum TodoServiceEvents {
    /**
     * @eventProperty
     * This RBEvent is send when the list of todos is updated
     */
    ON_TODOS_UPDATED = "ON_TODOS_UPDATED",
    /**
     * @eventProperty
     * This RBEvent is send when a todo is created
     */
    ON_TODO_CREATED = "ON_TODO_CREATED",
    /**
     * @eventProperty
     * This RBEvent is send when a todo is updated
     */
    ON_TODO_UPDATED = "ON_TODO_UPDATED",
    /**
     * @eventProperty
     * This RBEvent is send when a todo is deleted
     */
    ON_TODO_DELETED = "ON_TODO_DELETED",
    /**
     * @eventProperty
     * This RBEvent is send when a todo message map is modified
     */
    ON_TODO_MESSAGEMAP_MODIFIED = "ON_TODO_MESSAGEMAP_MODIFIED",
    /**
     * @eventProperty
     * This RBEvent is send when the TodoService status changes
     */
    ON_TODO_SERVICE_STATUS = "ON_TODO_SERVICE_STATUS",
    /**
     * @eventProperty
     * This RBEvent is send when a todo category is created
     */
    ON_TODO_CATEGORY_CREATED = "ON_TODO_CATEGORY_CREATED",
    /**
     * @eventProperty
     * This RBEvent is send when a todo category is updated
     */
    ON_TODO_CATEGORY_UPDATED = "ON_TODO_CATEGORY_UPDATED",
    /**
     * @eventProperty
     * This RBEvent is send when a todo category is deleted
     */
    ON_TODO_CATEGORY_DELETED = "ON_TODO_CATEGORY_DELETED"
}

export interface TodoService {
    /**
     * Indicates whether the Bubble Service has been started or not.
     * If this is not the case, no bubble can yet been retrieved.
     */
    started: boolean;

    /**
     * Subscribe to todo updates; All events send to the callback are of type {@link RBEvent}
     * @param callback - The callback function that will be called when events are received
     */
    subscribe(handler: (event: RBEvent<TodoServiceEvents>) => any, eventNames?: TodoServiceEvents | TodoServiceEvents[]): Subscription;

    /**
     * Creates a todo category
     * @param name - name of the category to be created
     * @param color - color of the category to be created as hexadecimal RGB value (e.g. #FFFFFF)
     * @returns The {@link TodoCategory} created or throws an error if category with the same name already exists or something bad happened
     */
    createCategory(name: string, color?: string): Promise<TodoCategory>;

    /**
     * Updates an existing todo category
     * @param category - {@link TodoCategory} to be updated
     * @param data - JSON object with optional properties name and color
     * @returns the updated {@link TodoCategory}
     */
    updateCategory(category: TodoCategory, data: { name?: string; color?: string; }): Promise<TodoCategory>;

    /**
     * Deletes an existing todo category
     * @param category - {@link TodoCategory} to delete
     */
    deleteCategory(category: TodoCategory): Promise<void>;

    /**
     * Get an existing {@link TodoCategory} by it's unique identifier
     * @param categoryId - ID of {@link TodoCategory} to retrieve
     * @returns The {@link TodoCategory} if found, undefined otherwise
     */
    getCategoryById(categoryId: string): TodoCategory | undefined;

    /**
     * Get an existing {@link TodoCategory} by it's unique name
     * @param categoryName - name of {@link TodoCategory} to retrieve
     * @returns The {@link TodoCategory} if found, undefined otherwise
     */
    getCategoryByName(categoryName: string): TodoCategory | undefined;

    /**
     * Creates a todo
     * @param content - content of todo as {@link TodoPersonalContent} or {@link ITodoMsgContent}
     * @param category - the {@link TodoCategory} to which the todo will be added
     * @param position - the position of the todo in the category. Defaults to 0 (first)
     * @returns The {@link TodoElem} created
     */
    createTodo(content: TodoPersonalContent | ITodoMsgContent, category: TodoCategory, position?: number): Promise<void>;

    /**
     * Changes the category to which the todo belongs
     * @param todo - the {@link TodoElem} to be updated
     * @param newCategory - the new {@link TodoCategory} to which the todo will be assigned
     * @returns The {@link TodoElem} updated
     *
     */
    updateTodoCategory(todo: TodoElem<TodoPersonalContent | ITodoMsgContent>, newCategory: TodoCategory): Promise<TodoElem<TodoPersonalContent | ITodoMsgContent>>;

    /**
     * Updates the content of a todo
     * @param todo  - the {@link TodoElem} to be updated
     * @param content - content of todo as {@link TodoPersonalContent} or {@link ITodoMsgContent}
     * @returns The {@link TodoElem} updated
     *
     */
    updateTodoContent(todo: TodoElem<TodoPersonalContent | ITodoMsgContent>, content: TodoPersonalContent | ITodoMsgContent): Promise<TodoElem<TodoPersonalContent | ITodoMsgContent>>;

    /**
     * Updates the position of a todo
     * @param todo  - the {@link TodoElem} to be updated
     * @param position - new position of todo in category
     * @returns The {@link TodoElem} updated
     *
     */
    updateTodoPosition(todo: TodoElem<TodoPersonalContent | ITodoMsgContent>, position: number): Promise<TodoElem<TodoPersonalContent | ITodoMsgContent>>;

    /**
     * Deletes a todo
     * @param todo - The {@link TodoElem} to be deleted
     */
    deleteTodo(todo: TodoElem<TodoPersonalContent | ITodoMsgContent>): Promise<void>;

    /**
     * Get all todos of connected user
     * @returns The list of {@link TodoElem} belonging to user
     */
    getAllTodos(): TodoElem<TodoPersonalContent | ITodoMsgContent>[];

    /**
     * Get all todos from a given category of connected user
     * @param category - the {@link TodoCategory} to retrieve
     * @returns The list of {@link TodoElem} of the given category belonging to user
     *
     */
    getAllTodosByCategory(category: TodoCategory): TodoElem<TodoPersonalContent | ITodoMsgContent>[];

    /**
     * Get all categories of connected user
     * @returns The list of {@link TodoCategory} belonging to user
     *
     */
    getAllCategories(): TodoCategory[];

    /**
     * Whether user can create a new category
     * @returns true if can, false otherwise
     */
    canCreateNewCategory(): boolean;

}

export class TodoServiceRB extends Service implements TodoService {
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private utilitiesService: UtilitiesService = UtilitiesService.getInstance();
    private conversationService: ConversationService = ConversationServiceRB.getInstance();

    private portalURL: string = config.restServerUrl + '/api/rainbow/enduser/v1.0/users';

    // Map structure that indexes todo elements with categoryId
    private todos = new Map<string, TodoElem<TodoPersonalContent | ITodoMsgContent>[]>();
    private rxSubject: Subject<any> = new Subject<any>();
    private listenerRef: any = null;
    private _started: boolean = false;
    public get started(): boolean {
        return this._started;
    }

    public set started(value: boolean) {
        this._started = value;
        this.sendEvent(TodoServiceEvents.ON_TODO_SERVICE_STATUS, this._started);
    }

    private categories = new Map<string, TodoCategory>(); // KV list with key being the identifier of the category

    public todoEnabled: boolean = false;
    private messageMap = new Map<MessageID, string>(); // KV list with key being messageId and value being the body text
    private subscription: Subscription = new Subscription();
    private todoMaxCategories: number = 0;

    public static getInstance() {
        return Service.create(TODO_SVC, TodoServiceRB);
    }

    public static build() {
        return new TodoServiceRB();
    }

    private constructor() {
        super();
    }

    /////////////////////////////////////////////
    // Interface implementations
    ////////////////////////////////////////////

    public getCategoryById(categoryId: string): TodoCategory | undefined {
        return this.categories.get(categoryId);
    }

    public getCategoryByName(categoryName: string): TodoCategory | undefined {
        let catKey = '';
        for (const [key, value] of this.categories) {
            if (value.name === categoryName) {
                catKey = key;
                break;
            }
        }
        return this.categories.get(catKey);
    }

    public async createCategory(name: string, color: string = ''): Promise<TodoCategory> {
        if (!this.canCreateNewCategory()) {
            const error = RBError.create(`[TodoService] createCategory - maximum number of categories (${this.todoMaxCategories}) reached`, RBErrorCode.TODO_MAX_CATEGORY_REACHED);
            error.detailsData = this.todoMaxCategories;
            throw error;
        }
        if (Boolean(name) === false) {
            throw new Error('[TodoService] createCategory - name is mandatory');
        }

        if (this.getCategoryByName(name) !== undefined) {
            throw new Error('[TodoService] createCategory - a category with this name already exist, try using TodoService.getCategoryByName(...) to retrieve it');
        }
        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/category`);

            const headers = this.authService.getPostHeader();
            const categoryData = { category: name };
            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body: JSON.stringify(categoryData) });

            this.logger.debug(`[TodoService] createCategory - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) {
                throw (response);
            }
            const todoListData = (await response.json()).data;
            let newCategory = new TodoCategory(todoListData.categoryId, name);
            this.addCategoryToCache(newCategory);
            if (color) {
                // Update category's assigned color if specified
                newCategory = await this.updateCategory(newCategory, { color: color });
            }

            return newCategory;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createCategory');
            this.logger.error(`[TodoService] ${errorInfo.message}`);
            throw errorInfo.error;

        }
    }

    public async updateCategory(category: TodoCategory, data: { name?: string; color?: string; }): Promise<TodoCategory> {
        if (this.getCategoryByName(category.name) === undefined) {
            throw new Error('[TodoService] updateCategory - this category does not exist, try using TodoService.createCategory(...) to create it');
        }
        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/properties/${category.id}`);

            const headers = this.authService.getPostHeader();
            const categoryData = { properties: data };
            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body: JSON.stringify(categoryData) });

            this.logger.debug(`[TodoService] updateCategory - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) {
                throw (response);
            }
            const todoListData = (await response.json()).data;
            category.name = todoListData?.category;
            category.color = todoListData?.properties?.color;
            this.updateCategoryInCache(category);
            return category;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateCategory');
            this.logger.error(`[TodoService] ${errorInfo.message}`);
            throw errorInfo.error;

        }
    }

    public async deleteCategory(category: TodoCategory): Promise<void> {
        if (this.getCategoryByName(category.name) === undefined) {
            throw new Error('[TodoService] deleteCategory - this category does not exist');
        }
        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/category/${category.id}`);

            const headers = this.authService.getRequestHeader();

            const response: Response = await fetch(url.toString(), { method: 'DELETE', headers });

            this.logger.debug(`[TodoService] deleteCategory - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) {
                throw (response);
            }
            this.deleteCategoryFromCache(category);
            return;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteCategory');
            this.logger.error(`[TodoService] ${errorInfo.message}`);
            throw errorInfo.error;

        }
    }

    public async createTodo(content: TodoPersonalContent | ITodoMsgContent, category: TodoCategory, position?: number): Promise<void> {
        if (Boolean(content) === false || Boolean(category) === false) {
            throw new Error('[TodoService] createTodo - content of todo and category are mandatory');
        }
        try {
            if (!content || !category) {
                throw new Error("[TodoServiceRB] createTodo -- missing content or category");
            }
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos`);
            const headers = this.authService.getPostHeader();
            const newTodoData = { category: category.name, content: content, position: position };
            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body: JSON.stringify(newTodoData) });

            this.logger.debug(`[TodoService] createTodo - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) throw (response);
            return;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createTodo');
            this.logger.error(`[TodoServiceRB] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updateTodoCategory(todo: TodoElem<TodoPersonalContent | ITodoMsgContent>, newCategory: TodoCategory): Promise<TodoElem<TodoPersonalContent | ITodoMsgContent>> {
        if (Boolean(todo) === false || Boolean(newCategory) === false) {
            throw new Error('[TodoService] updateTodoCategory - content of todo and newCategory are mandatory');
        }

        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/${todo.id}`);
            const headers = this.authService.getPostHeader();
            const newTodoData: { category: string; content: any; position: number; } = { category: newCategory.name, content: todo.content, position: todo.position };
            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(newTodoData) });

            this.logger.debug(`[TodoService] updateTodoCategory - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) throw (response);
            const todoData = (await response.json()).data;
            this.updateTodo(newCategory.name, todo.id, todo.content, todo.position);
            todo.categoryId = todoData?.category;
            return todo;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateTodoCategory');
            this.logger.error(`[TodoService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updateTodoContent(todo: TodoElem<TodoPersonalContent | ITodoMsgContent>, content: TodoPersonalContent | ITodoMsgContent): Promise<TodoElem<TodoPersonalContent | ITodoMsgContent>> {
        if (Boolean(todo) === false || Boolean(content) === false) {
            throw new Error('[TodoService] updateTodoContent - content of todo and content are mandatory');
        }

        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/${todo.id}`);
            const headers = this.authService.getPostHeader();
            const newTodoData: { category: string; content: any; position: number; } = { category: todo.categoryId, content: content, position: todo.position };
            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(newTodoData) });

            this.logger.debug(`[TodoService] updateTodoContent - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) throw (response);
            const todoData = (await response.json()).data;
            this.updateTodo(todo.categoryId, todo.id, todoData?.todo?.content, todo.position);
            todo.content = todoData?.todo?.content;
            return todo;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateTodoContent');
            this.logger.error(`[TodoService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updateTodoPosition(todo: TodoElem<TodoPersonalContent | ITodoMsgContent>, position: number): Promise<TodoElem<TodoPersonalContent | ITodoMsgContent>> {
        if (Boolean(todo) === false || Boolean(position) === false) {
            throw new Error('[TodoService] updateTodoPosition - content of todo and content are mandatory');
        }

        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/${todo.id}`);
            const headers = this.authService.getPostHeader();
            const newTodoData: { category: string; content: any; position: number; } = { category: todo.categoryId, content: todo.content, position: position };
            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(newTodoData) });

            this.logger.debug(`[TodoService] updateTodoPosition - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) throw (response);
            const todoData = (await response.json()).data;
            this.updateTodo(todo.categoryId, todo.id, todo.content, todoData?.todo?.position);
            todo.position = todoData?.todo?.position;
            return todo;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateTodoPosition');
            this.logger.error(`[TodoService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async deleteTodo(todo: TodoElem<TodoPersonalContent | ITodoMsgContent>): Promise<void> {
        if (Boolean(todo) === false) {
            throw new Error('[TodoService] deleteTodo - todo is mandatory');
        }
        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/${todo.id}`);
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url.toString(), { method: 'DELETE', headers });
            this.logger.debug(`[TodoService] deleteTodo - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);
            if (!response.ok) {
                throw (response);
            }

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteTodo');
            this.logger.error(`[TodoService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public getAllTodos(): TodoElem<TodoPersonalContent | ITodoMsgContent>[] {
        return Array.from(this.todos.values()).flat();
    }

    public getAllTodosByCategory(category: TodoCategory): TodoElem<TodoPersonalContent | ITodoMsgContent>[] {
        return this.todos.get(category.id);
    }

    public getAllCategories(): TodoCategory[] {
        return [...this.categories.values()];
    }

    public getCategoriesMap(): Map<string, TodoCategory> {
        return this.categories;
    }

    public canCreateNewCategory(): boolean {
        return this.todoMaxCategories > this.categories.size;
    }

    /**
     * @public
     * Allows to subscribe to instance's RX subject
     * @param handler - The handler function to be called when event is fired
     * @returns the subscription
     */
    public subscribe(handler: any): Subscription {
        return this.rxSubject.subscribe(handler);
    }

    /**
     * @internal
     * Allows to send an event (of type RBEvent) via the RX subject of the instance, and to be captured by subscribe
     * @param name - The name of the event
     * @param data - The data

     */
    private sendEvent(name: string, data: any = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    public async start(): Promise<void> {
        try {
            const todoMaxItems = this.profileService.getFeatureLimitMax(this.profileService.FeaturesEnum.TO_DO_LIST_MAX_ITEMS);
            this.todoMaxCategories = this.profileService.getFeatureLimitMax(this.profileService.FeaturesEnum.TO_DO_LIST_CATEGORIES);
            this.todoEnabled = this.contactService.userContact?.instantMessagesEnabled && todoMaxItems > 0;
            if (this.todoEnabled) {
                await this.retrieveUserTodoList();
                try {
                    await this.retrieveAllMessageDataOfTodos();
                    this.subscription.add(this.conversationService.subscribe(
                        (event: RBEvent) => {
                            if (event?.data?.message && this.messageMap.has(event?.data?.message.id)) {
                                this.updateMapElement(event?.data?.message.id, event?.data?.message.data);
                            }
                        }, ConversationServiceEvents.ON_MESSAGE_MODIFIED_IN_CONVERSATION
                    ));
                } catch (error) {
                    // I don't care if this part fails
                }
                if (this.xmppService.connected) { this.attachHandlers(); }
                this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
                    if (event.name === 'ON_XMPP_CONNECTED') {
                        if (event.data.reason === 'RECONNECTION' && this.started) { /**/ }
                        else {
                            this.attachHandlers();
                        }
                    }
                    if (event.name === 'ON_XMPP_DISCONNECTED' && event.data.reason === 'LOGOUT') { this.started = false; }
                });
            } else {
                this.logger.info(`[TodoServiceRB] === start skipped - Not allowed to use : ${todoMaxItems}`);
                return;
            }
        } catch (error) {
            this.logger.error('[TodoServiceRB] === STARTING FAILURE === ' + error?.message);
            this.reconnect();
        }
    }

    public async stop(): Promise<void> {
        if (this.listenerRef) {
            this.xmppService.connection.deleteHandler(this.listenerRef);
            this.listenerRef = null;
        }
        this.messageMap.clear();
        this.todos.clear();
        this.categories.clear();
        this.started = false;
    }

    public async reconnect(attempt: number = 0): Promise<void> {
        try {
            this.logger.info('[TodoServiceRB] reconnect');
            attempt++;

            // Clean subscription for a fresh restart
            this.subscription.unsubscribe();

            const todoMaxItems = this.profileService.getFeatureLimitMax(this.profileService.FeaturesEnum.TO_DO_LIST_MAX_ITEMS);
            this.todoEnabled = this.contactService.userContact?.instantMessagesEnabled && todoMaxItems > 0;
            if (this.todoEnabled) {
                await this.retrieveUserTodoList();
                try {
                    await this.retrieveAllMessageDataOfTodos();
                    this.subscription.add(this.conversationService.subscribe(
                        (event: RBEvent) => {
                            if (event?.data?.message && this.messageMap.has(event?.data?.message.id)) {
                                this.updateMapElement(event?.data?.message.id, event?.data?.message.data);
                            }
                        }, ConversationServiceEvents.ON_MESSAGE_MODIFIED_IN_CONVERSATION
                    ));
                } catch (error) {
                    // I don't care if this part fails
                }

                if (this.xmppService.connected) { this.attachHandlers(); }
                this.xmppService.subscribeToConnectionEvents((event: RBEvent) => {
                    if (event.name === 'ON_XMPP_CONNECTED') {
                        if (event.data.reason === 'RECONNECTION' && this.started) { /**/ }
                        else {
                            this.attachHandlers();
                        }
                    }
                    if (event.name === 'ON_XMPP_DISCONNECTED' && event.data.reason === 'LOGOUT') { this.started = false; }
                });
            } else {
                this.logger.info(`[TodoServiceRB] === reconnect skipped - Not allowed to use : ${todoMaxItems}`);
                return;
            }
        } catch (error) {
            this.logger.error('[TodoServiceRB] reconnect failure -- ' + error?.message);
            if (attempt === 1) { this.reconnect(attempt); }
        }
    }

    private attachHandlers() {
        this.logger.info('[TodoServiceRB] attachHandlers');
        if (this.listenerRef) {
            this.xmppService.connection.deleteHandler(this.listenerRef);
            this.listenerRef = null;
        }
        this.listenerRef = this.xmppService.connection.addHandler((stanza: any) => { this.onConfigMessage(stanza); return true; }, "jabber:iq:configuration", "message", "management");
        this.started = true;
    }

    /**
     * @param id - id of element to fetch
     * @returns the element found or null
     */
    public getTodoById(id: string): TodoElem<TodoPersonalContent | ITodoMsgContent> | null {
        let result: TodoElem<TodoPersonalContent | ITodoMsgContent> | null = null;

        for (const todoElems of this.todos.values()) {
            for (const todoElem of todoElems) {
                if (todoElem.id === id) {
                    result = todoElem;
                    break;
                }
            }
            if (result) break;
        }
        return result;
    }

    /**
     * Adds a todo item to the internal list of todos
     * @param categoryId - the category identifier to which the todo belongs
     * @param todoId - Unique identifier of the todo
     * @param content - data of the todo item
     * @param position - of the item in the category

     */
    private addTodo(categoryId: string, todoId: string, content: any, position: number): void {
        if (this.getTodoById(todoId)) {
            this.logger.warn(`[TodoServiceRB] Adding an already existing todo, updating it instead: ${todoId}`);
            this.updateTodo(categoryId, todoId, content, position);
        } else {
            const newElem = TodoElem.createFromData(todoId, content, categoryId, position);
            if (this.todos.has(categoryId)) {
                const todoList = this.todos.get(categoryId);
                todoList.splice(position, 0, newElem);
            } else {
                this.todos.set(categoryId, [newElem]);
            }
            this.updateItemsPositionValue(this.todos.get(categoryId));
            if (content?.messageId && !this.messageMap.has(content.messageId)) {
                this.addMapElement(content.messageId);
            }
            this.sendEvent(TodoServiceEvents.ON_TODO_CREATED, newElem);
        }
    }

    /**
     *  @internal
     * Updates a todo item to the internal list of todos
     * @param categoryId - the category identifier to which the todo belongs
     * @param todoId - Unique identifier of the todo
     * @param content - data of the todo item
     * @param position - of the item in the category

     */
    private updateTodo(categoryId: string, todoId: string, content: any, position: number): void {
        const todoItem = this.getTodoById(todoId);
        if (todoItem) {
            if (todoItem.categoryId !== categoryId) {
                // Remove from old category
                let todoList = this.todos.get(todoItem.categoryId);
                const ind = todoList.findIndex(todoElem => todoElem.id === todoId);
                if (ind > -1) {
                    todoList.splice(ind, 1);
                    this.updateItemsPositionValue(todoList);
                }
                // Add to new category at given position
                if (this.todos.has(categoryId)) {
                    todoList = this.todos.get(categoryId);
                    todoList.splice(position, 0, todoItem);
                } else {
                    this.todos.set(categoryId, [todoItem]);
                }
                this.updateItemsPositionValue(todoList);

            } else if (todoItem.position !== position) {
                // same category but position change
                this.utilitiesService.arrayMove(this.todos.get(categoryId), todoItem.position, position);
                this.updateItemsPositionValue(this.todos.get(categoryId));
            }
            todoItem.updateData(content, categoryId, position);
            this.sendEvent(TodoServiceEvents.ON_TODO_UPDATED, todoItem);
        } else {
            this.logger.warn(`[TodoServiceRB] Updating an unknown todo: ${todoId}`);
            this.addTodo(categoryId, todoId, content, position);
        }
    }

    /**
     *
     *  @internal
     * Updates position parameter of each item in array to reflect position in array
     * @param arr  - array to modify

     */
    private updateItemsPositionValue(arr: TodoElem<TodoPersonalContent | ITodoMsgContent>[]) {
        arr.forEach((todoItem, index) => {
            if (todoItem) todoItem.position = index;
        });
    }

    /**
     *  @internal
     * Deletes a todo item to the internal list of todos
     * @param id - Unique identifier of the todo

     */
    private deleteTodoInternal(id: string): void {
        let deleted: boolean = false;
        for (const todoElems of this.todos.values()) {
            const ind = todoElems.findIndex((todoElem) => {
                return todoElem.id === id;
            });
            if (ind > -1) {
                const deletedItem = todoElems.splice(ind, 1)[0];
                deleted = true;
                if (deletedItem.content && this.messageMap.has((deletedItem.content as TodoMsgContent | TodoRoomMsgContent).messageId)) {
                    this.removeMapElement((deletedItem.content as TodoMsgContent | TodoRoomMsgContent).messageId);
                }
            }
            if (deleted) break;
        }
        this.sendEvent(TodoServiceEvents.ON_TODO_DELETED, id);
    }

    /**
     *  @internal
     * Handler function for XMPP messages
     * @param stanza - received

     */
    private async onConfigMessage(stanza: string) {
        try {
            const todoElem = $(stanza).find('todo');
            if (todoElem.length) {
                const type = todoElem.attr('type');
                switch (type) {
                    case 'item': // event for toto item
                        this.onTodoConfigMessage(todoElem);
                        break;
                    case 'properties': // event for category properties (e.g. color)
                    case 'category': // event for category elem
                        this.onCategoryConfigMessage(todoElem);
                        break;
                    default:
                        this.logger.warn(`[TodoServiceRB] onConfigMessage -- Warning -- Unhandled type ${type}`);
                        break;
                }
            }
        } catch (error) {
            this.logger.error(`[TodoServiceRB] onConfigMessage -- Failure -- ${error}`);
        }

    }

    private onTodoConfigMessage(todoElem: any) {
        const todoId = todoElem.attr('id');
        const todoPosition = todoElem.attr('position');
        const todoCategoryId = todoElem.attr('categoryId');
        const todoContent = todoElem.find('content').text();
        const action = todoElem.attr('action');

        this.logger.debug(`[TodoServiceRB] onTodoConfigMessage -- ${action} - ${todoId}`);
        switch (action) {
            case "create":
                this.addTodo(todoCategoryId, todoId, JSON.parse(todoContent), Number(todoPosition));
                break;
            case "update":
                this.updateTodo(todoCategoryId, todoId, JSON.parse(todoContent), Number(todoPosition));
                break;
            case "delete":
                this.deleteTodoInternal(todoId);
                break;
            default:
                break;
        }
    }

    private onCategoryConfigMessage(todoElem: any) {
        const action = todoElem.attr('action');
        const name = todoElem.attr('category');
        const categoryId = todoElem.attr('categoryId') ? todoElem.attr('categoryId') : todoElem.attr('id');
        const categoryContent = todoElem.find('content').text();
        this.logger.debug(`[TodoServiceRB] onCategoryConfigMessage -- ${action} - ${name} - ${categoryId}`);
        switch (action) {
            case "create": {
                if (categoryId) {
                    const newCategory = new TodoCategory(categoryId, name);
                    this.addCategoryToCache(newCategory);
                }
                break;
            }
            case "update": {
                const existingCategory = this.getCategoryById(categoryId) ? this.getCategoryById(categoryId) : this.getCategoryByName(name);
                const color = JSON.parse(categoryContent)?.color;
                if (existingCategory) {
                    if (color) existingCategory.color = color;
                    this.updateCategoryInCache(existingCategory);
                } else {
                    this.logger.warn(`[TodoServiceRB] Category ${name} / ${categoryId} not known, trying to add it`);
                    const newCategory = new TodoCategory(categoryId, name);
                    if (color) newCategory.color = color;
                    this.addCategoryToCache(newCategory);
                }
                break;
            }
            case "delete": {
                const deletedCategory = this.getCategoryById(categoryId) ? this.getCategoryById(categoryId) : this.getCategoryByName(name);
                if (deletedCategory) {
                    this.deleteCategoryFromCache(deletedCategory);
                } else {
                    this.logger.warn(`[TodoServiceRB] Category ${name} / ${categoryId} already deleted`);
                }
                break;
            }
            default:
                break;
        }

    }

    /**
     * @public
     * Handler function for XMPP messages
     * @returns the list of todo

     */
    public getTodos(): Map<string, TodoElem<TodoPersonalContent | ITodoMsgContent>[]> {
        return this.todos;
    }

    /**
     * @public
     * Retrieve the list of todos of the connected user and store it locally
     * @param category - optional category to specify which category to retrieve
     * @returns the list of todo retrieved
     * @fires TodoEvent#ON_TODOS_UPDATED with local list is updated

     */
    public async retrieveUserTodoList(category: string = ""): Promise<Map<string, TodoElem<TodoPersonalContent | ITodoMsgContent>[]>> {
        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos`);
            if (category) { url.searchParams.set(`category`, category); }
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            this.logger.debug(`[TodoServiceRB] retrieveUserTodoList - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) {
                throw (response);
            }
            const todoListData = (await response.json()).data;
            const userTodoList: any[] = todoListData.todos;

            // Parse the results and store it locally
            if (userTodoList?.length) {
                userTodoList.forEach((group: any) => {
                    const categoryName = group.category;
                    const categoryColor = group.properties?.color;
                    const categoryId = group.categoryId;
                    this.addCategoryToCache(new TodoCategory(categoryId, categoryName, categoryColor));
                    this.todos.set(categoryId, []);
                    const categoryItems: TodoElem<TodoPersonalContent | ITodoMsgContent>[] = [];
                    const categoryTodos: any[] = group.list;
                    if (categoryTodos?.length) {
                        categoryTodos.forEach((todoElem: any, index: number) => {
                            const elem = TodoElem.createFromData(todoElem.id, todoElem.content, categoryId, index);
                            categoryItems.push(elem);
                        });
                        this.todos.set(categoryId, categoryItems);
                    }
                });
                this.sendEvent(TodoServiceEvents.ON_TODOS_UPDATED, this.todos);
            }

            this.logger.debug(`[TodoServiceRB] retrieveUserTodoList -- SUCCESS -- `);
            return this.todos;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveUserTodoList');
            this.logger.error(`[TodoServiceRB] ${errorInfo.message}`);
            throw errorInfo.error;

        }
    }

    /**
     * @public
     * retrieves a todo element from server
     * @param todoId - Identifier of the todo to retrieve
     * @returns   resolves with the todo as created on server

     */
    public async retrieveTodo(todoId: string): Promise<TodoElem<TodoPersonalContent | ITodoMsgContent>> {
        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/${todoId}`);
            const headers = this.authService.getRequestHeader();
            const response: Response = await fetch(url.toString(), { method: 'GET', headers });
            this.logger.debug(`[TodoServiceRB] retrieveTodo - RequestId: ${response.headers.get(`x-rainbow-request-id`)} status: ${response.status}`);

            if (!response.ok) {
                throw (response);
            }
            const todoData = (await response.json()).data;
            const todo = TodoElem.createFromData(todoData.todo.id, todoData.todo.content, todoData.category, todoData.position);
            this.logger.debug(`[TodoServiceRB] retrieveTodo -- SUCCESS -- `);
            return todo;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveTodo');
            this.logger.error(`[TodoServiceRB] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * @public
     * updates a todo element on server
     * @param todoId - Unique identifier of the todo
     * @param content - data of the todo item
     * @param category - the {@link TodoCategory} to which the todo belongs
     * @param position - position of the item in the category
     * @returns  resolves with the todo as updated on server

     */
    public async updateTodoElem(todoId: string, content: any, category: TodoCategory, position: number = -1): Promise<TodoElem<TodoPersonalContent | ITodoMsgContent>> {
        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/todos/${todoId}`);
            const headers = this.authService.getPostHeader();
            const newTodoData: { category: string; content: any; position?: number; } = { category: category.name, content: content };
            if (position > -1) { newTodoData.position = position; }
            this.updateTodo(category.id, todoId, content, position);
            const response: Response = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(newTodoData) });
            if (!response.ok) throw (response);
            const todoData = (await response.json()).data;
            const todo = TodoElem.createFromData(todoData.todo.id, todoData.todo.content, todoData.category, todoData.position);
            this.logger.debug(`[TodoServiceRB] updateTodoElem -- SUCCESS -- `);
            return todo;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateTodoElem');
            this.logger.error(`[TodoServiceRB] ${errorInfo.message}`);
            throw errorInfo.error;
        }

    }

    /**
     * @public
     * Allows user to retrieve it's ims by list of message Ids, peer and peer type This API can only be used by user himself (i.e. userId of logged in user = value of userId parameter in URL) If message cannot be retrieved response will return status not found
     * @param ims - array of ims where
     *  type: Conversation type ("user", "room", "bot")
     *  peer: jid with which message has been exchanged
     *  msgId: xmpp message id of the message to retrieve
     * @returns resolves with the ims retrieved

     */
    public async retrieveIms(ims: { type: string; peer: string; msgId: MessageID; around: number; }[]): Promise<any> {
        if (ims.length < 1) return [];
        try {
            const url = new URL(`${this.portalURL}/${this.contactService.userContact.dbId}/ims`);
            const headers = this.authService.getPostHeader();
            const response: Response = await fetch(url.toString(), { method: 'POST', headers, body: JSON.stringify({ ims: ims }) });
            if (!response.ok) throw (response);
            const imsData = (await response.json()).data;
            this.logger.debug(`[TodoServiceRB] retrieveIms -- SUCCESS -- `);
            return imsData;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'retrieveIms');
            this.logger.error(`[TodoServiceRB] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * @public
     * Retrieve message data of all messages saved in todo list and save them in todo as displayText
     * @returns resolves with the ims retrieved
     */
    public async retrieveAllMessageDataOfTodos() {
        if (this.todos.size === 0) return;
        const requestData: { type: string; peer: string; msgId: MessageID; around: number; }[] = [];
        this.todos.forEach((todoElems: TodoElem<TodoPersonalContent | ITodoMsgContent>[]) => {
            todoElems.forEach((todoElem: TodoElem<any>) => {
                if (!todoElem.content.personalNote) {
                    const data: { type: string; peer: string; msgId: MessageID; around: number; } = {
                        type: todoElem.content.roomId ? "room" : "user",
                        peer: todoElem.content.conversationJid,
                        msgId: todoElem.content.messageId,
                        around: todoElem.content.messageTimestamp
                    };
                    requestData.push(data);
                }
            });
        });

        const ims: any[] = await this.retrieveIms(requestData);
        ims.forEach((elem) => {
            if (elem.status !== 'not_found') { this.messageMap.set(elem.msgId, this.extractTextFromXmpp(elem.xml)); }
        });
        this.syncMessageMapToTodo();
    }

    private syncMessageMapToTodo() {
        let msgCount = 0;
        let personalNoteCount = 0;

        this.todos.forEach((todoElems: TodoElem<TodoPersonalContent | ITodoMsgContent>[]) => {
            msgCount = personalNoteCount = 0;
            todoElems.forEach((todoElem: TodoElem<any>) => {
                if (todoElem.content.personalNote) personalNoteCount++;
                if (todoElem.content.messageId && this.messageMap.has(todoElem.content.messageId)) {
                    todoElem.displayText = this.messageMap.get(todoElem.content.messageId);
                    msgCount++;
                }
            });
            this.logger.debug(`[TodoServiceRB] - syncMessageMapToTodo - loaded ${msgCount} messages with ${personalNoteCount} personal for ${todoElems.length}`);
        });
    }

    private extractTextFromXmpp(stanza: any): string {
        try {
            const text = xmlParser(stanza).find('body').text();
            return text ? text : "";
        } catch (error) {
            this.logger.warn(`[TodoServiceRB] extractTextFromXmpp - ${error.message}`);
            return "";
        }
    }

    public isMessageSaved(messageId: MessageID) {
        return this.messageMap.has(messageId);
    }

    public getSavedMessageTodoColor(messageId: MessageID): string {

        let savedTodoElem: TodoElem<TodoPersonalContent | ITodoMsgContent>;
        mainLoop: for (const todoElems of this.todos.values()) {
            for (const todoElem of todoElems) {
                if (todoElem.content && (todoElem.content as ITodoMsgContent).messageId === messageId) {
                    savedTodoElem = todoElem;
                    break mainLoop;
                }
            }
        }
        if (savedTodoElem) {
            const category = this.categories.get(savedTodoElem.categoryId);
            return category.color;
        }
        return '';
    }

    public addMapElement(key: MessageID, value: string = "") {
        this.messageMap.set(key, value);
        this.syncMessageMapToTodo();
        this.sendEvent(TodoServiceEvents.ON_TODO_MESSAGEMAP_MODIFIED, key);
    }

    public updateMapElement(key: MessageID, value: string = "") {
        this.messageMap.set(key, value);
        this.syncMessageMapToTodo();
        this.sendEvent(TodoServiceEvents.ON_TODO_MESSAGEMAP_MODIFIED, key);
    }

    public removeMapElement(key: MessageID) {
        this.messageMap.delete(key);
        this.syncMessageMapToTodo();
        this.sendEvent(TodoServiceEvents.ON_TODO_MESSAGEMAP_MODIFIED, key);
    }

    public getNumberOfTodos(): number {
        let count = 0;

        this.todos.forEach((todoElems: TodoElem<TodoPersonalContent | ITodoMsgContent>[]) => {
            count += todoElems.length;
        });
        return count;
    }

    /**
     * @public
     * Removes a message that was added to todo list
     * @param messageId - ID of message to delete from todo list
     * @returns resolves if success, throws an error otherwise
     */
    public async removeMessageFromTodo(messageId: MessageID): Promise<void> {
        for (const todoElems of this.todos.values()) {
            for (const todoElem of todoElems) {
                if (todoElem.content && (todoElem.content as ITodoMsgContent).messageId === messageId) {
                    return this.deleteTodo(todoElem);
                }
            }
        }
        throw new Error(`Cannot find message ${messageId} to delete`);
    }

    ///////////////////////////////
    // Internal methods ///////////

    private addCategoryToCache(category: TodoCategory) {
        if (!category) {
            this.logger.error(`[TodoServiceRB] No category to add`);
            return;
        }
        const existingCategory = this.getCategoryById(category.id) ? this.getCategoryById(category.id) : this.getCategoryByName(category.name);
        if (existingCategory) {
            this.logger.debug(`[TodoServiceRB] Category ${category.name} / ${category.id} already in cache`);
            return;
        }
        this.categories.set(category.id, category);
        this.todos.set(category.id, []);
        this.sendEvent(TodoServiceEvents.ON_TODO_CATEGORY_CREATED, { category: category });
    }

    private deleteCategoryFromCache(category: TodoCategory) {
        if (this.categories.delete(category.id) && this.todos.delete(category.id))
            this.sendEvent(TodoServiceEvents.ON_TODO_CATEGORY_DELETED, { category: category });
    }

    private updateCategoryInCache(category: TodoCategory) {
        if (!category) {
            this.logger.error(`[TodoServiceRB] No category to update`);
            return;
        }
        const existingCategory = this.getCategoryById(category.id) ? this.getCategoryById(category.id) : this.getCategoryByName(category.name);
        if (existingCategory) {
            existingCategory.color = category.color;
            existingCategory.name = category.name;
            this.categories.set(existingCategory.id, existingCategory);
            this.sendEvent(TodoServiceEvents.ON_TODO_CATEGORY_UPDATED, { category: existingCategory });
            return;
        }
        this.logger.warn(`[TodoServiceRB] Category ${category.name} / ${category.id} not known, trying to add it`);
        this.addCategoryToCache(category);
    }
}
